import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { tasksAPI } from '../services/api';
import { Task } from '../types';
import TaskDetailSidebar from './TaskDetailSidebar';
import TaskModal from './TaskModal';
import { showCustomAlert } from './CustomAlert';
import { useAuth } from '../contexts/AuthContext';

interface ProjectTasksListProps {
  projectId: string;
}

const ProjectTasksList: React.FC<ProjectTasksListProps> = ({ projectId }) => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  useEffect(() => {
    loadTasks();
  }, [projectId]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const data = await tasksAPI.getAll({ projectId });
      const tasksList = Array.isArray(data) ? data : (data as any).items || [];
      setTasks(tasksList);
    } catch (err: any) {
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = () => {
    setEditingTask(null);
    setTaskModalVisible(true);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setTaskModalVisible(true);
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
  };

  const statusOptions = [
    { label: 'Za rad', value: 'todo' },
    { label: 'U toku', value: 'in_progress' },
    { label: 'Završeno', value: 'done' }
  ];

  const priorityOptions = [
    { label: 'Nizak', value: 'low' },
    { label: 'Srednji', value: 'medium' },
    { label: 'Visok', value: 'high' }
  ];

  const canChangeStatus = (task: Task) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'direktor') return true;
    return task.assignees?.some((assignee) => assignee._id === user._id) || false;
  };

  const statusBodyTemplate = (rowData: Task) => {
    const statusMap: Record<string, { label: string; severity: 'success' | 'info' | 'warning' | 'danger' }> = {
      todo: { label: 'Za rad', severity: 'info' },
      in_progress: { label: 'U toku', severity: 'warning' },
      done: { label: 'Završeno', severity: 'success' }
    };
    const status = statusMap[rowData.status] || { label: rowData.status, severity: 'info' };

    if (!canChangeStatus(rowData)) {
      return <Tag value={status.label} severity={status.severity} />;
    }

    return (
      <Dropdown
        value={rowData.status}
        options={statusOptions}
        onChange={async (e) => {
          try {
            await tasksAPI.updateStatus(rowData._id, e.value);
            await loadTasks();
          } catch (err) {
            console.error('Error updating status:', err);
          }
        }}
        appendTo="self"
        style={{ minWidth: '120px' }}
      />
    );
  };

  const priorityBodyTemplate = (rowData: Task) => {
    const priorityMap: Record<string, { label: string; severity: 'success' | 'info' | 'warning' | 'danger' }> = {
      low: { label: 'Nizak', severity: 'info' },
      medium: { label: 'Srednji', severity: 'warning' },
      high: { label: 'Visok', severity: 'danger' }
    };
    const priority = priorityMap[rowData.priority] || { label: rowData.priority, severity: 'info' };
    
    if (!canChangeStatus(rowData)) {
      return <Tag value={priority.label} severity={priority.severity} />;
    }
    
    return (
      <Dropdown
        value={rowData.priority}
        options={priorityOptions}
        onChange={async (e) => {
          try {
            await tasksAPI.update(rowData._id, { priority: e.value });
            await loadTasks();
          } catch (err) {
            console.error('Error updating priority:', err);
          }
        }}
        appendTo="self"
        style={{ minWidth: '120px' }}
      />
    );
  };

  const assigneesBodyTemplate = (rowData: Task) => {
    if (!rowData.assignees || rowData.assignees.length === 0) return '-';
    return (
      <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
        {rowData.assignees.slice(0, 4).map((assignee) => (
          assignee.avatarUrl ? (
            <img
              key={assignee._id}
              src={assignee.avatarUrl}
              alt={`${assignee.firstName} ${assignee.lastName}`}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #e5e7eb'
              }}
              title={`${assignee.firstName} ${assignee.lastName}`}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = document.createElement('div');
                fallback.style.cssText = 'width: 32px; height: 32px; border-radius: 50%; background-color: #9ca3af; color: white; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: bold;';
                fallback.textContent = `${assignee.firstName[0]}${assignee.lastName[0]}`;
                target.parentNode?.appendChild(fallback);
              }}
            />
          ) : (
            <div
              key={assignee._id}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#9ca3af',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}
              title={`${assignee.firstName} ${assignee.lastName}`}
            >
              {assignee.firstName[0]}{assignee.lastName[0]}
            </div>
          )
        ))}
        {rowData.assignees.length > 4 && (
          <div style={{ fontSize: '0.875rem', color: '#666' }}>+{rowData.assignees.length - 4}</div>
        )}
      </div>
    );
  };

  const deadlineBodyTemplate = (rowData: Task) => {
    if (!rowData.deadline) return '-';
    return new Date(rowData.deadline).toLocaleDateString('sr-RS');
  };

  const creatorBodyTemplate = (rowData: Task) => {
    return `${rowData.createdBy.firstName} ${rowData.createdBy.lastName}`;
  };

  const canEditTask = (task: Task) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'direktor') return true;
    return task.createdBy?._id === user._id;
  };

  const actionsBodyTemplate = (rowData: Task) => {
    const canEdit = canEditTask(rowData);
    return (
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        {canEdit ? (
          <i
            className="pi pi-pencil"
            style={{ 
              color: '#6b7280', 
              cursor: 'pointer', 
              fontSize: '1rem',
              transition: 'opacity 0.2s'
            }}
            onClick={() => handleEditTask(rowData)}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            title="Izmeni"
          />
        ) : (
          <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>—</span>
        )}
        {canEdit && (
          <i
            className="pi pi-trash"
            style={{ 
              color: '#6b7280', 
              cursor: 'pointer', 
              fontSize: '1rem',
              transition: 'opacity 0.2s'
            }}
            onClick={() => {
              showCustomAlert.confirm(
                'Da li ste sigurni da želite da obrišete zadatak?',
                async () => {
                  try {
                    await tasksAPI.delete(rowData._id);
                    await loadTasks();
                  } catch (err) {
                    console.error('Error deleting task:', err);
                    showCustomAlert.error('Greška pri brisanju zadatka');
                  }
                },
                'Potvrda brisanja'
              );
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            title="Obriši"
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          label="Add task"
          icon="pi pi-plus"
          className="p-button-success"
          onClick={handleAddTask}
        />
      </div>

      <div style={{ overflow: 'visible' }}>
        <DataTable
          value={tasks}
          loading={loading}
          emptyMessage="Nema zadataka"
          onRowClick={(e) => handleTaskClick(e.data as Task)}
          style={{ cursor: 'pointer' }}
          scrollable={false}
        >
        <Column field="name" header="Zadatak" sortable />
        <Column field="assignees" header="Dodeljeno" body={assigneesBodyTemplate} />
        <Column field="deadline" header="Rok" body={deadlineBodyTemplate} sortable />
        <Column field="estimatedHours" header="Očekivano vreme" />
        <Column field="createdBy" header="Kreator" body={creatorBodyTemplate} />
        <Column field="status" header="Status" body={statusBodyTemplate} />
        <Column field="priority" header="Prioritet" body={priorityBodyTemplate} />
        <Column body={actionsBodyTemplate} header="Akcije" style={{ width: '120px' }} />
      </DataTable>
      </div>

      {taskModalVisible && (
        <TaskModal
          visible={taskModalVisible}
          onHide={() => {
            setTaskModalVisible(false);
            setEditingTask(null);
          }}
          projectId={projectId}
          task={editingTask}
          onSave={loadTasks}
        />
      )}

      {selectedTask && (
        <TaskDetailSidebar
          task={selectedTask}
          visible={!!selectedTask}
          onHide={() => setSelectedTask(null)}
          onTaskUpdate={loadTasks}
        />
      )}
    </div>
  );
};

export default ProjectTasksList;
